import { useDrag } from "@use-gesture/react"
import { Fragment, useMemo, useRef, useState } from "react"
import type { CanvasEvent, InteractionEvents, ShapeEvent } from "./events"
import { Paths } from "./lib/paths"
import { Shape } from "./lib/processor"

type Props = {
  cellSize: number
  rows: number
  cols: number
  lineOffset: number
  shapes: Shape[]
  padding: number
} & InteractionEvents

/**
 * Component which renders Flowgram data as an SVG
 */
export function FlowgramInteractor(props: Props) {
  const [svg, setSVG] = useState<SVGSVGElement>(null!)
  const pointerShape = useRef<Shape>(null)
  const dragState = useRef<{
    start: CanvasEvent
    current: CanvasEvent
  }>(null)

  useDrag(
    (e) => {
      const event: CanvasEvent = initShapeEvent({ clientX: e.xy[0], clientY: e.xy[1] }, pointerShape.current)
      if (e.first) {
        dragState.current = {
          start: event,
          current: event,
        }
        props.onDragStart?.(event)
      } else if (e.last) {
        dragState.current = null
        props.onDragEnd?.(event)
      } else {
        dragState.current!.current = event
      }
    },
    {
      target: svg,
    },
  )

  function initShapeEvent(e: { clientX: number; clientY: number }, shape: Shape | null): CanvasEvent {
    const bounds = svg.getBoundingClientRect()
    return {
      shape: shape || undefined,
      clientX: e.clientX,
      clientY: e.clientY,
      x: e.clientX - bounds.left,
      y: e.clientY - bounds.top,
      col: Math.floor(((e.clientX - bounds.left) / bounds.width) * props.cols),
      row: Math.floor(((e.clientY - bounds.top) / bounds.height) * props.rows),
      dragging: !!dragState.current,
      dragStart: dragState.current?.start,
    }
  }

  const lastEmitted = useMemo(() => ({ name: "", col: -1, row: -1, dragging: false, hovering: false }), [])
  function shouldEmit(name: string, event: CanvasEvent | ShapeEvent) {
    for (const key in lastEmitted) {
      if ((lastEmitted as any)[key] !== (event as any)[key]) {
        ;(lastEmitted as any)[key] = (event as any)[key]
        return true
      }
    }
    return false
  }

  const layers = new Map<number, Shape[]>()

  for (const shape of props.shapes) {
    if (!layers.has(shape.layerId)) {
      layers.set(shape.layerId, [shape])
    } else {
      layers.get(shape.layerId)!.push(shape)
    }
  }

  return (
    <svg
      ref={(svg) => setSVG(svg!)}
      xmlns="http://www.w3.org/2000/svg"
      width={props.cols * props.cellSize + props.padding * 2}
      height={props.rows * props.cellSize + props.padding * 2}
      viewBox={`${-props.padding} ${-props.padding} ${
        props.cols * props.cellSize + props.padding * 2
      } ${props.rows * props.cellSize + props.padding * 2}`}
      onPointerMove={(e) => {
        const event = initShapeEvent(e, pointerShape.current) as CanvasEvent
        if (shouldEmit("move", event)) props.onMove?.(event)
      }}
      style={{
        touchAction: "none",
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 15,
      }}
    >
      {[...layers.entries()].map(([layerId, shapes]) => {
        return (
          <g key={layerId}>
            {/* Draw the interactive layer */}
            {shapes.map((shape, i) => {
              return (
                <Fragment key={i}>
                  {/* Invisible but interactive shape */}
                  <path
                    d={Paths.stringify(shape.getPath(props.cellSize, props.lineOffset))}
                    // stroke="red"
                    fill={"rgba(0,0,0,0)"}
                    // stroke="black"
                    onPointerEnter={(e) => {
                      pointerShape.current = shape
                      const event = initShapeEvent(e, shape) as ShapeEvent
                      event.hovering = true
                      props.onShapeHover?.(event)
                    }}
                    onPointerLeave={(e) => {
                      pointerShape.current = null
                      const event = initShapeEvent(e, shape) as ShapeEvent
                      event.hovering = true
                      props.onShapeHover?.(event)
                    }}
                  />
                </Fragment>
              )
            })}
          </g>
        )
      })}
    </svg>
  )
}
